import React, { useEffect, useMemo, useState } from "react";
import { api, downloadFile, formatINR, todayISO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import StatCard from "@/components/StatCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus,
  Trash2,
  Pencil,
  Save,
  TrendingUp,
  TrendingDown,
  Download,
  Landmark,
  ArrowDownToLine,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_COLORS = {
  "Mutual Funds": "bg-indigo-600",
  Stocks: "bg-emerald-600",
  Gold: "bg-amber-500",
  Bonds: "bg-slate-700",
};

const emptyForm = {
  date: todayISO(),
  category: "Mutual Funds",
  name: "",
  vehicle_id: "__none",
  total_invested: "",
  current_value: "",
  remark: "",
};

const emptyRedForm = {
  date: todayISO(),
  category: "Mutual Funds",
  amount: "",
  remark: "",
};

export default function Investments() {
  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);

  const [filterCategory, setFilterCategory] = useState("__all");
  const [filterVehicle, setFilterVehicle] = useState("__all");

  const [openForm, setOpenForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  // Redemptions state
  const [redemptions, setRedemptions] = useState([]);
  const [redSummary, setRedSummary] = useState(null);
  const [openRedForm, setOpenRedForm] = useState(false);
  const [editingRedId, setEditingRedId] = useState(null);
  const [redForm, setRedForm] = useState(emptyRedForm);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/investment-categories").then((r) =>
      setCategories(r.data.categories || [])
    );
  }, []);

  const load = () => {
    const params = {};
    if (filterCategory !== "__all") params.category = filterCategory;
    if (filterVehicle !== "__all") params.vehicle_id = filterVehicle;
    api.get("/investments", { params }).then((r) => setItems(r.data));
    api.get("/investments/summary").then((r) => setSummary(r.data));
    const redParams = filterCategory !== "__all" ? { category: filterCategory } : {};
    api.get("/redemptions", { params: redParams }).then((r) => setRedemptions(r.data));
    api.get("/redemptions/summary").then((r) => setRedSummary(r.data));
  };

  useEffect(load, [filterCategory, filterVehicle]);

  const vehicleMap = useMemo(() => {
    const m = {};
    vehicles.forEach((v) => (m[v.id] = v));
    return m;
  }, [vehicles]);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm, date: todayISO() });
    setOpenForm(true);
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      date: it.date,
      category: it.category,
      name: it.name || "",
      vehicle_id: it.vehicle_id || "__none",
      total_invested: String(it.total_invested ?? ""),
      current_value: String(it.current_value ?? ""),
      remark: it.remark || "",
    });
    setOpenForm(true);
  };

  const onSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name / instrument is required");
      return;
    }
    if (!form.date) {
      toast.error("Date is required");
      return;
    }
    const payload = {
      date: form.date,
      category: form.category,
      name: form.name.trim(),
      vehicle_id: form.vehicle_id === "__none" ? null : form.vehicle_id,
      total_invested: parseFloat(form.total_invested || 0),
      current_value: parseFloat(form.current_value || 0),
      remark: form.remark.trim(),
    };
    try {
      if (editingId) {
        await api.put(`/investments/${editingId}`, payload);
        toast.success("Investment updated");
      } else {
        await api.post("/investments", payload);
        toast.success("Investment added");
      }
      setOpenForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this investment?")) return;
    await api.delete(`/investments/${id}`);
    toast.success("Deleted");
    load();
  };

  const onDownload = async () => {
    const params = new URLSearchParams();
    if (filterCategory !== "__all") params.set("category", filterCategory);
    if (filterVehicle !== "__all") params.set("vehicle_id", filterVehicle);
    const qs = params.toString();
    const url = `/export/investments${qs ? `?${qs}` : ""}`;
    try {
      await downloadFile(url, "investments.xlsx");
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  // Redemption handlers
  const openAddRed = () => {
    setEditingRedId(null);
    setRedForm({ ...emptyRedForm, date: todayISO() });
    setOpenRedForm(true);
  };
  const openEditRed = (r) => {
    setEditingRedId(r.id);
    setRedForm({
      date: r.date,
      category: r.category,
      amount: String(r.amount ?? ""),
      remark: r.remark || "",
    });
    setOpenRedForm(true);
  };
  const onSaveRed = async () => {
    if (!redForm.date) return toast.error("Date required");
    if (!redForm.amount || parseFloat(redForm.amount) <= 0)
      return toast.error("Amount required");
    const payload = {
      date: redForm.date,
      category: redForm.category,
      amount: parseFloat(redForm.amount),
      remark: (redForm.remark || "").trim(),
    };
    try {
      if (editingRedId) {
        await api.put(`/redemptions/${editingRedId}`, payload);
        toast.success("Redemption updated");
      } else {
        await api.post("/redemptions", payload);
        toast.success("Redemption added");
      }
      setOpenRedForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const onDeleteRed = async (id) => {
    if (!window.confirm("Delete this redemption?")) return;
    await api.delete(`/redemptions/${id}`);
    toast.success("Deleted");
    load();
  };

  const gainColor = (v) =>
    v > 0 ? "text-emerald-700" : v < 0 ? "text-red-600" : "text-gray-700";

  return (
    <div className="space-y-6" data-testid="investments-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Portfolio</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-1">
            Investments
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Track Mutual Funds, Stocks, Gold & Bonds. Not counted in operational profit.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onDownload}
            className="gap-2"
            data-testid="download-investments-btn"
          >
            <Download className="h-4 w-4" /> Excel
          </Button>
          <Button
            onClick={openAdd}
            className="gap-2"
            data-testid="add-investment-btn"
          >
            <Plus className="h-4 w-4" /> Add Investment
          </Button>
        </div>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Invested"
          value={formatINR(summary?.total_invested || 0)}
          sub={`${summary?.count || 0} holdings`}
          testid="stat-total-invested"
        />
        <StatCard
          label="Current Value"
          value={formatINR(summary?.total_current || 0)}
          accent="text-blue-700"
          sub="Latest updated value"
          testid="stat-current-value"
        />
        <StatCard
          label="Gain / Loss"
          value={formatINR(summary?.gain_loss || 0)}
          accent={gainColor(summary?.gain_loss || 0)}
          sub={
            (summary?.gain_loss || 0) >= 0
              ? "Unrealised gain"
              : "Unrealised loss"
          }
          testid="stat-gain-loss"
        />
        <StatCard
          label="Return %"
          value={`${(summary?.gain_pct || 0).toFixed(2)}%`}
          accent={gainColor(summary?.gain_pct || 0)}
          sub="Overall portfolio"
          testid="stat-gain-pct"
        />
      </div>

      {/* Per category */}
      {summary?.per_category?.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="per-category">
          {summary.per_category.map((row) => (
            <div
              key={row.category}
              className="card-swiss p-4"
              data-testid={`cat-card-${row.category}`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`h-2 w-2 rounded-full ${
                    CATEGORY_COLORS[row.category] || "bg-gray-400"
                  }`}
                />
                <div className="label-eyebrow">{row.category}</div>
              </div>
              <div className="mt-2 font-display text-2xl font-black tracking-tight">
                {formatINR(row.current)}
              </div>
              <div className="text-xs text-gray-500 mt-0.5 font-mono">
                Invested {formatINR(row.invested)}
              </div>
              <div
                className={`mt-2 flex items-center gap-1 text-sm font-semibold ${gainColor(
                  row.gain_loss
                )}`}
              >
                {row.gain_loss >= 0 ? (
                  <TrendingUp className="h-3.5 w-3.5" />
                ) : (
                  <TrendingDown className="h-3.5 w-3.5" />
                )}
                {formatINR(row.gain_loss)}{" "}
                <span className="text-xs opacity-70">
                  ({row.gain_pct.toFixed(2)}%)
                </span>
              </div>
              <div className="text-[11px] text-gray-500 mt-1">
                {row.count} holding{row.count !== 1 ? "s" : ""}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="card-swiss p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Category</Label>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger
                className="mt-1"
                data-testid="filter-category-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">Vehicle</Label>
            <Select value={filterVehicle} onValueChange={setFilterVehicle}>
              <SelectTrigger
                className="mt-1"
                data-testid="filter-vehicle-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All (linked & personal)</SelectItem>
                <SelectItem value="__none">Personal (no vehicle)</SelectItem>
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name} · {v.registration_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card-swiss p-5" data-testid="investments-list">
        <div className="label-eyebrow mb-3">Holdings ({items.length})</div>
        {items.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-500">
            <Landmark className="h-8 w-8 mx-auto text-gray-300" />
            <p className="mt-2">No investments yet. Add your first holding.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="py-2 pr-3 font-semibold">Date</th>
                  <th className="py-2 px-2 font-semibold">Category</th>
                  <th className="py-2 px-2 font-semibold">Name</th>
                  <th className="py-2 px-2 font-semibold">Vehicle</th>
                  <th className="py-2 px-2 text-right font-semibold">Invested</th>
                  <th className="py-2 px-2 text-right font-semibold">Current</th>
                  <th className="py-2 px-2 text-right font-semibold">Gain/Loss</th>
                  <th className="py-2 px-2 text-right font-semibold">%</th>
                  <th className="py-2 pl-2 font-semibold">Remark</th>
                  <th className="py-2 pl-2 text-right font-semibold w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const inv = Number(it.total_invested || 0);
                  const cur = Number(it.current_value || 0);
                  const gl = cur - inv;
                  const pct = inv > 0 ? (gl / inv) * 100 : 0;
                  const v = it.vehicle_id ? vehicleMap[it.vehicle_id] : null;
                  return (
                    <tr
                      key={it.id}
                      className="border-b border-gray-100 hover-row"
                      data-testid={`investment-row-${it.id}`}
                    >
                      <td className="py-3 pr-3 font-mono text-xs">{it.date}</td>
                      <td className="py-3 px-2">
                        <span
                          className={`text-[11px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm text-white ${
                            CATEGORY_COLORS[it.category] || "bg-gray-500"
                          }`}
                        >
                          {it.category}
                        </span>
                      </td>
                      <td className="py-3 px-2 font-semibold">{it.name}</td>
                      <td className="py-3 px-2 text-xs">
                        {v ? (
                          <>
                            {v.name}
                            <div className="text-[11px] text-gray-500 font-mono">
                              {v.registration_number}
                            </div>
                          </>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right font-mono">
                        {formatINR(inv)}
                      </td>
                      <td className="py-3 px-2 text-right font-mono">
                        {formatINR(cur)}
                      </td>
                      <td
                        className={`py-3 px-2 text-right font-mono font-semibold ${gainColor(
                          gl
                        )}`}
                      >
                        {formatINR(gl)}
                      </td>
                      <td
                        className={`py-3 px-2 text-right font-mono ${gainColor(pct)}`}
                      >
                        {pct.toFixed(2)}%
                      </td>
                      <td className="py-3 pl-2 text-xs text-gray-600 max-w-[220px] truncate">
                        {it.remark || "—"}
                      </td>
                      <td className="py-3 pl-2 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => openEdit(it)}
                            data-testid={`edit-investment-${it.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => onDelete(it.id)}
                            data-testid={`delete-investment-${it.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Redemptions */}
      <div className="card-swiss p-5" data-testid="redemptions-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
          <div>
            <div className="label-eyebrow">Redemptions</div>
            <h3 className="font-display text-xl font-bold tracking-tight mt-1">
              Withdrawals log
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Total redeemed:{" "}
              <span className="font-mono font-semibold text-gray-900">
                {formatINR(redSummary?.total || 0)}
              </span>{" "}
              · {redSummary?.count || 0} entr
              {(redSummary?.count || 0) === 1 ? "y" : "ies"}
            </p>
          </div>
          <Button
            onClick={openAddRed}
            variant="outline"
            className="gap-2"
            data-testid="add-redemption-btn"
          >
            <ArrowDownToLine className="h-4 w-4" /> Add Redemption
          </Button>
        </div>

        {redSummary?.per_category?.some((c) => c.amount > 0) && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            {redSummary.per_category.map((c) => (
              <div
                key={c.category}
                className="border border-gray-200 rounded-md px-3 py-2"
                data-testid={`red-cat-${c.category}`}
              >
                <div className="flex items-center gap-1.5">
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      CATEGORY_COLORS[c.category] || "bg-gray-400"
                    }`}
                  />
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                    {c.category}
                  </div>
                </div>
                <div className="font-mono text-sm font-semibold mt-1">
                  {formatINR(c.amount)}
                </div>
              </div>
            ))}
          </div>
        )}

        {redemptions.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500">
            <ArrowDownToLine className="h-7 w-7 mx-auto text-gray-300" />
            <p className="mt-2">No redemptions yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="py-2 pr-3 font-semibold">Date</th>
                  <th className="py-2 px-2 font-semibold">Category</th>
                  <th className="py-2 px-2 text-right font-semibold">Amount</th>
                  <th className="py-2 pl-2 font-semibold">Remark</th>
                  <th className="py-2 pl-2 text-right font-semibold w-20">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {redemptions.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-gray-100 hover-row"
                    data-testid={`redemption-row-${r.id}`}
                  >
                    <td className="py-3 pr-3 font-mono text-xs">{r.date}</td>
                    <td className="py-3 px-2">
                      <span
                        className={`text-[11px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm text-white ${
                          CATEGORY_COLORS[r.category] || "bg-gray-500"
                        }`}
                      >
                        {r.category}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right font-mono font-semibold">
                      {formatINR(r.amount)}
                    </td>
                    <td className="py-3 pl-2 text-xs text-gray-600 max-w-[280px] truncate">
                      {r.remark || "—"}
                    </td>
                    <td className="py-3 pl-2 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEditRed(r)}
                          data-testid={`edit-redemption-${r.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => onDeleteRed(r.id)}
                          data-testid={`delete-redemption-${r.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Redemption dialog */}
      <Dialog open={openRedForm} onOpenChange={setOpenRedForm}>
        <DialogContent data-testid="redemption-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editingRedId ? "Edit Redemption" : "New Redemption"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={redForm.date}
                  onChange={(e) =>
                    setRedForm({ ...redForm, date: e.target.value })
                  }
                  data-testid="redemption-date-input"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={redForm.category}
                  onValueChange={(v) =>
                    setRedForm({ ...redForm, category: v })
                  }
                >
                  <SelectTrigger data-testid="redemption-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                inputMode="decimal"
                value={redForm.amount}
                onChange={(e) =>
                  setRedForm({ ...redForm, amount: e.target.value })
                }
                placeholder="0"
                data-testid="redemption-amount-input"
              />
            </div>
            <div>
              <Label>Remark (optional)</Label>
              <Textarea
                rows={2}
                value={redForm.remark}
                onChange={(e) =>
                  setRedForm({ ...redForm, remark: e.target.value })
                }
                placeholder="Optional note"
                data-testid="redemption-remark-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={onSaveRed}
              className="gap-2"
              data-testid="save-redemption-btn"
            >
              <Save className="h-4 w-4" />{" "}
              {editingRedId ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent data-testid="investment-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editingId ? "Edit Investment" : "New Investment"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="investment-date-input"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger data-testid="investment-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Name / Instrument</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. HDFC Small Cap, Reliance, SGB 2023-II"
                data-testid="investment-name-input"
              />
            </div>
            <div>
              <Label>Vehicle (optional)</Label>
              <Select
                value={form.vehicle_id}
                onValueChange={(v) => setForm({ ...form, vehicle_id: v })}
              >
                <SelectTrigger data-testid="investment-vehicle-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Personal (no vehicle)</SelectItem>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name} · {v.registration_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Total Invested (₹)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={form.total_invested}
                  onChange={(e) =>
                    setForm({ ...form, total_invested: e.target.value })
                  }
                  placeholder="0"
                  data-testid="investment-invested-input"
                />
              </div>
              <div>
                <Label>Current Value (₹)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={form.current_value}
                  onChange={(e) =>
                    setForm({ ...form, current_value: e.target.value })
                  }
                  placeholder="0"
                  data-testid="investment-current-input"
                />
              </div>
            </div>
            <div>
              <Label>Remark</Label>
              <Textarea
                rows={2}
                value={form.remark}
                onChange={(e) => setForm({ ...form, remark: e.target.value })}
                placeholder="Broker, units, notes…"
                data-testid="investment-remark-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={onSave}
              className="gap-2"
              data-testid="save-investment-btn"
            >
              <Save className="h-4 w-4" />{" "}
              {editingId ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
